<template>
  <div class="sidebar-page">
    <!-- Logo + version stay pinned while the menu scrolls -->
    <div class="sidebar-logo-header">
      <img height="150px" src="@/assets/payroll_logo.png" alt="App Logo" />
      <p class="is-small" size="small">{{ versionStr() }}</p>
    </div>

    <section class="sidebar-layout">
      <o-sidebar
        position="static"
        :mobile="mobile"
        :expand-on-hover="expandOnHover"
        :reduce="reduce"
        open
        activeClass="sidebar-item-active"
        contentClass="sidebar-content"
      >
        <section class="my-menu" style="padding: 0.1em">
          <section v-if="appStore.thisPeriod?.uuid" class="period-badge">
            <div class="period-header">
              <span class="period-cycle">{{ appStore.thisPayCycle }}</span>
              <i
                :class="'mdi ml-1 ' + (appStore.thisPeriod.status === 'Open' ? 'mdi-lock-open-outline' : 'mdi-lock-outline has-text-grey')"
                style="font-size: 1.4rem;"
              ></i>
              <i v-if="appStore.thisPeriod.generated_payslips"
                class="mdi mdi-check-circle has-text-success ml-1"
                style="font-size: 1.4rem;"
                title="Payslips generated"
              ></i>
            </div>
            <p class="period-dates">
              {{ dayjs(appStore.thisPeriod.start_date).format('MMM DD') }}
              &ndash;
              {{ dayjs(appStore.thisPeriod.end_date).format('MMM DD, YYYY') }}
            </p>
          </section>

          <o-menu itemActiveClass="menu-item-active">
            <o-menu-list class="is-size-6">
              <!-- Payroll -->
              <o-menu-item icon="calendar-account-outline" size="medium" expanded>
                <template #label="props">
                  Payroll

                  <o-icon
                    class="is-pulled-right"
                    size="medium"
                    rootClass="mdi-36px"
                    :icon="props.expanded ? 'chevron-up' : 'chevron-down'"
                  ></o-icon>
                </template>
                <o-menu-item
                  icon="calendar-month-outline"
                  size="medium"
                  label="Payroll Period"
                  @click="router.push('/period')"
                ></o-menu-item>
                <o-menu-item
                  icon="clock-outline"
                  size="medium"
                  label="Daily Time"
                  @click="router.push('/dailytime/all')"
                ></o-menu-item>
                <o-menu-item
                  icon="account-credit-card-outline"
                  size="medium"
                  label="Leaves"
                  @click="router.push('/leave/all')"
                >
                </o-menu-item>
                <o-menu-item
                  icon="calendar-plus"
                  size="medium"
                  label="Leave Credits"
                  data-cy="leave-credits"
                  @click="router.push('/leave-credits')"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="cash-refund"
                  size="medium"
                  label="Leave Conversion"
                  data-cy="leave-conversion"
                  @click="router.push('/leave-conversion')"
                ></o-menu-item>
                <o-menu-item
                  icon="account-credit-card-outline"
                  size="medium"
                  label="Earnings"
                  @click="router.push('/transact/earning')"
                ></o-menu-item>
                <o-menu-item
                  icon="account-credit-card-outline"
                  size="medium"
                  label="Deductions"
                  @click="router.push('/transact/deduction')"
                ></o-menu-item>

                <o-menu-item
                  icon="card-account-details-outline"
                  size="medium"
                  label="Payslips"
                  @click="router.push('/payslip')"
                ></o-menu-item>
              </o-menu-item>

              <!-- Report -->
              <o-menu-item icon="calendar-account-outline" size="medium">
                <template #label="props">
                  Report

                  <o-icon
                    class="is-pulled-right"
                    size="medium"
                    rootClass="mdi-36px"
                    :icon="props.expanded ? 'chevron-up' : 'chevron-down'"
                  ></o-icon>
                </template>
                <o-menu-item
                  icon="calendar-month-outline"
                  size="medium"
                  label="Payroll Register"
                  @click="loadReport('/preview/register/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="account-details-outline"
                  size="medium"
                  label="Employee Accumulated Totals"
                  data-cy="report-accumulated"
                  @click="loadReport('/preview/accumulated/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item
                  icon="calendar-month-outline"
                  size="medium"
                  label="Project Costing"
                  @click="loadReport('/preview/project/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item
                  icon="bank-outline"
                  size="medium"
                  label="Payroll Bank Report"
                  data-cy="report-bank"
                  @click="loadReport('/preview/bank/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item
                  icon="format-list-bulleted"
                  size="medium"
                  label="Earnings / Deductions Items"
                  data-cy="report-account"
                  @click="loadReport('/preview/account/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="gift-outline"
                  size="medium"
                  label="13th Month Pay (General)"
                  data-cy="report-13th"
                  @click="loadReport('/preview/thirteenth/all')"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="account-cash-outline"
                  size="medium"
                  label="13th Month Pay (Employee)"
                  data-cy="report-13th-employee"
                  @click="loadReport('/preview/thirteenth_employee/all')"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="calendar-account-outline"
                  size="medium"
                  label="Leave Ledger (Summary)"
                  data-cy="report-leave-ledger"
                  @click="loadReport('/preview/leave_ledger/all')"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="calendar-text-outline"
                  size="medium"
                  label="Leave Ledger (Employee)"
                  data-cy="report-leave-ledger-employee"
                  @click="loadReport('/preview/leave_ledger_employee/all')"
                ></o-menu-item>
                <o-menu-item
                  icon="cash-check"
                  size="medium"
                  label="Leave Conversion"
                  data-cy="report-leave-conversion"
                  @click="loadReport('/preview/leave_conversion/all')"
                ></o-menu-item>
                <!-- monthly: only on the month's final pay cycle -->
                <o-menu-item
                  icon="shield-account-outline"
                  size="medium"
                  label="SS Monthly Contribution"
                  :disabled="!monthlyReportsAvailable"
                  :title="monthlyReportsTitle"
                  data-cy="report-ss-monthly"
                  @click="loadReport('/preview/ss_monthly/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item
                  icon="home-account"
                  size="medium"
                  label="Pagibig Monthly Contribution"
                  :disabled="!monthlyReportsAvailable"
                  :title="monthlyReportsTitle"
                  data-cy="report-pagibig-monthly"
                  @click="loadReport('/preview/pagibig_monthly/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item
                  icon="hospital-box-outline"
                  size="medium"
                  label="PhilHealth Monthly Contribution"
                  :disabled="!monthlyReportsAvailable"
                  :title="monthlyReportsTitle"
                  data-cy="report-philhealth-monthly"
                  @click="loadReport('/preview/philhealth_monthly/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item
                  icon="percent-outline"
                  size="medium"
                  label="Withholding Tax (Monthly)"
                  :disabled="!monthlyReportsAvailable"
                  :title="monthlyReportsTitle"
                  data-cy="report-wtax-monthly"
                  @click="loadReport('/preview/wtax_monthly/' + appStore.thisPeriod.uuid)"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="format-list-numbered"
                  size="medium"
                  label="Alphalist (Annual)"
                  data-cy="report-tax-alphalist"
                  @click="loadReport('/preview/tax_alphalist/all')"
                ></o-menu-item>
                <o-menu-item v-if="authStore.isSupervisorUp"
                  icon="file-certificate-outline"
                  size="medium"
                  label="BIR 2316 (Employee)"
                  data-cy="report-tax-2316"
                  @click="loadReport('/preview/tax_2316/all')"
                ></o-menu-item>
              </o-menu-item>

              <!-- Setup -->
              <o-menu-item icon="format-list-checks" size="medium">
                <template #label="props">
                  Setup
                  <o-icon
                    class="is-pulled-right"
                    size="medium"
                    :icon="props.expanded ? 'chevron-up' : 'chevron-down'"
                  ></o-icon>
                </template>
                <o-menu-item
                  v-if="authStore.isAdmin"
                  icon="cog-outline"
                  size="medium"
                  label="Payroll Settings"
                  data-cy="menu-settings"
                  @click="router.push('/setup/settings')"
                ></o-menu-item>
                <o-menu-item
                  v-if="authStore.isAdmin"
                  icon="monitor-lock"
                  size="medium"
                  label="Devices"
                  data-cy="menu-devices"
                  @click="router.push('/setup/devices')"
                ></o-menu-item>
                <o-menu-item
                  icon="bank-check"
                  size="medium"
                  label="Customers"
                  @click="router.push('/company/customer')"
                ></o-menu-item>

                <o-menu-item
                  v-if="appStore.options.costEnabled"
                  icon="bank-check"
                  size="medium"
                  label="Cost Centers"
                  @click="router.push('/cost')"
                >
                </o-menu-item>
                <o-menu-item
                  icon="account-group-outline"
                  size="medium"
                  label="Departments"
                  @click="router.push('/department')"
                ></o-menu-item>
                <o-menu-item
                  icon="account-multiple-outline"
                  size="medium"
                  label="Employees"
                  @click="router.push('/employee')"
                ></o-menu-item>

                <o-menu-item
                  icon="hand-coin-outline"
                  size="medium"
                  label="Loans"
                  @click="router.push('/loan')"
                ></o-menu-item>

                <o-menu-item
                  icon="account-credit-card-outline"
                  size="medium"
                  label="Accounts"
                  @click="router.push('/account')"
                ></o-menu-item>
                <o-menu-item
                  icon="clock-outline"
                  size="medium"
                  label="Schedules"
                  @click="router.push('/schedule')"
                ></o-menu-item>
                <o-menu-item
                  icon="file-chart-outline"
                  size="medium"
                  label="Holidays"
                  @click="router.push('/table/holiday')"
                ></o-menu-item>
                <o-menu-item
                  icon="file-chart-outline"
                  size="medium"
                  label="SS Table"
                  @click="router.push('/table/ss')"
                ></o-menu-item>
                <o-menu-item
                  icon="file-chart-outline"
                  size="medium"
                  label="BIR Table"
                  @click="router.push('/table/bir')"
                ></o-menu-item>
                <o-menu-item
                  icon="file-chart-outline"
                  size="medium"
                  label="PhilHealth Table"
                  @click="router.push('/table/philhealth')"
                ></o-menu-item>
                <o-menu-item
                  icon="file-chart-outline"
                  size="medium"
                  label="Pagibig Table"
                  @click="router.push('/table/pagibig')"
                ></o-menu-item>

                <!-- <o-menu-item icon="account-multiple-outline" label="Pay Code"  @click="router.push('/paycode')"></o-menu-item> -->
              </o-menu-item>

              <!-- Admin -->
              <o-menu-item icon="cog" size="medium">
                <template #label="props">
                  Admin
                  <o-icon
                    class="is-pulled-right"
                    size="medium"
                    :icon="props.expanded ? 'chevron-up' : 'chevron-down'"
                  ></o-icon>
                </template>

                <o-menu-item icon="fingerprint" size="medium" label="System Users" @click="router.push('/system/users')"></o-menu-item>
                <o-menu-item v-if="authStore.isManagement" icon="clipboard-text-clock-outline" size="medium" label="Activity Log" data-cy="menu-activity" @click="router.push('/system/activity')"></o-menu-item>
              </o-menu-item>

              <!-- the signed-in user's email as plain text under a separator line, then My Account -->
              <li class="menu-separator" role="separator"></li>
              <li class="menu-user-email" data-cy="account-email" :title="authStore.currentUser?.email || ''">{{
                authStore.currentUser?.email || ''
              }}</li>
              <o-menu-item icon="account-tie" size="medium" data-cy="menu-account">
                <template #label="props">
                  My Account
                  <o-icon
                    class="is-pulled-right"
                    size="medium"
                    :icon="props.expanded ? 'chevron-up' : 'chevron-down'"
                  ></o-icon>
                </template>
                <o-menu-item icon="account-details-outline" label="Edit Profile" @click="router.push('/my-account/profile')"></o-menu-item>
                <o-menu-item icon="account-key-outline" label="Change Password" @click="router.push('/my-account/password')"></o-menu-item>
              </o-menu-item>
            </o-menu-list>
            <o-menu-list class="is-size-6">
              <o-menu-item icon="logout-variant" size="medium" label="Logout" @click="authStore.logout()"></o-menu-item>
            </o-menu-list>
          </o-menu>
        </section>
      </o-sidebar>
    </section>
  </div>
</template>

<script setup lang="ts">
import { defineComponent, ref, reactive, nextTick, computed } from 'vue'
import router from '@/router'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { usePeriodStore } from '@/stores/period'
// @ts-ignore
import { APP_VERSION } from '../assets/version.js'
import dayjs from 'dayjs'

const expandOnHover = ref(false)
const mobile = ref('reduced')
const reduce = ref(false)
const appStore = useAppStore()
const authStore = useAuthStore()

const versionStr = () => {
  const v = APP_VERSION.split('|')
  return v[0] + '(' + v[1].slice(1, 8) + ')'
}

const periodStore = usePeriodStore()
const monthlyReportsAvailable = computed(() =>
  periodStore.isFinalPeriodOfMonth(appStore.thisPeriod)
)
const monthlyReportsTitle = computed(() =>
  monthlyReportsAvailable.value
    ? ''
    : 'Available when the final pay cycle of the month is selected'
)

const loadReport = async (reportUrl: string) => {
  await router.push(reportUrl).then(() => {
    setTimeout(() => {
      window.location.reload()
    }, 200)
  })
}

// export default defineComponent({
//   setup() {
//     const expandOnHover = ref(false)
//     const mobile = ref('reduced')
//     const reduce = ref(false)
//     const appStore = useAppStore()

//     const versionStr = () => {
//       const v = APP_VERSION.split('|')
//       return v[0] + '(' + v[1].slice(1, 8) + ')'
//     }

//     return {
//       expandOnHover,
//       versionStr,
//       mobile,
//       reduce,
//       router,
//       appStore
//     }
//   }
// })
</script>

<style scoped>
.menu-separator {
  list-style: none;
  height: 0;
  margin: 0.6rem 0.5rem;
  border-top: 1px solid rgba(165, 180, 252, 0.35);
}
.menu-user-email {
  list-style: none;
  margin: 0 0.5rem;
  padding: 0.1rem 0.25rem;
  min-height: 1.1rem;
  font-size: 0.8rem;
  color: #a5b4fc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: text;
}
.sidebar-page {
  display: flex;
  flex-direction: column;
  max-width: 300px;
  height: 100%;
}
.sidebar-layout {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  scrollbar-width: thin;
  scrollbar-color: rgba(224, 231, 255, 0.35) transparent;
}
.sidebar-layout::-webkit-scrollbar {
  width: 4px;
}
.sidebar-layout::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-layout::-webkit-scrollbar-thumb {
  background: rgba(224, 231, 255, 0.35);
  border-radius: 2px;
}
.sidebar-layout::-webkit-scrollbar-thumb:hover {
  background: rgba(224, 231, 255, 0.6);
}

.my-menu {
  background-color: #1e1b4b;
  border-top-right-radius: 0 !important;
}

/* keep Oruga's inner wrapper dark so wave overlay in App.vue shows on top */
.sidebar-page :deep(.o-side__content),
.sidebar-page :deep([class*="o-side"]) {
  background-color: #1e1b4b !important;
  overflow-y: visible !important;
}

.sidebar-logo-header {
  flex-shrink: 0;
  background-color: #1e1b4b;
  padding: 0.1em;
  text-align: center;
}
.sidebar-logo-header .is-small {
  color: #a5b4fc;
  font-size: 0.9rem;
}

img {
  border-radius: 0;
  opacity: 0.92;
}

/* ── Period badge ── */
.period-badge {
  background: rgba(99, 102, 241, 0.25);
  border: 1px solid rgba(165, 180, 252, 0.35);
  border-radius: 0 !important;
  padding: 0.5em 0.9em;
  margin: 0 0 0.4em 0;
  font-size: 0.95rem;
}
.period-cycle {
  font-weight: 700;
  font-size: 1rem;
  color: #a5b4fc;
  margin-bottom: 0.15em;
}
.period-dates {
  font-size: 0.9rem;
  color: #c7d2fe;
  margin-bottom: 0.15em;
}
.period-header {
  display: flex;
  align-items: center;
  gap: 0.2em;
}

/* ── Menu links ── */
.my-menu :deep(.menu-list a) {
  display: flex;
  align-items: center;
  gap: 0.75em;
  padding: 5px 8px;
  color: #e0e7ff;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
}
.my-menu :deep(.menu-list li) {
  margin-bottom: 0;
}
.my-menu :deep(.menu-list a:hover) {
  background-color: rgba(165, 180, 252, 0.18);
  color: #ffffff;
}
.my-menu :deep(.menu-list a.is-active),
.my-menu :deep(.menu-item-active) {
  background-color: #4f46e5;
  color: #fff;
}

/* ── Menu icons ── */
.my-menu :deep(.icon) {
  width: 1.1em !important;
  height: 1.1em !important;
}
.my-menu :deep(.icon .mdi),
.my-menu :deep(.icon svg) {
  color: #c7d2fe;
  font-size: 1rem !important;
}

/* ── Version string ── */
.my-menu .is-small {
  color: #a5b4fc;
  font-size: 0.75rem;
}

/* ── Section header expand/collapse chevrons ── */
.my-menu :deep(.menu-list .label) {
  color: #ffffff;
  font-weight: 600;
}
</style>
